import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { BookOpen, Users, Leaf } from 'lucide-react';
import BrandMark from '@/components/BrandMark';

export default function AuthPage() {
  const { login, register } = useAuth();
  const [tab, setTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [regForm, setRegForm] = useState({
    email: '',
    password: '',
    family_name: '',
    children: [{ name: '', year_level: '5' }],
  });
  const [termsAccepted, setTermsAccepted] = useState(false);

  const updateChild = (idx, field, value) => {
    setRegForm(p => ({ ...p, children: p.children.map((c, i) => i === idx ? { ...c, [field]: value } : c) }));
  };
  const addChild = () => {
    setRegForm(p => ({ ...p, children: [...p.children, { name: '', year_level: '5' }] }));
  };
  const removeChild = (idx) => {
    setRegForm(p => ({ ...p, children: p.children.filter((_, i) => i !== idx) }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success('Kia ora! Welcome back');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      toast.error('Something went wrong — please try again');
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      toast.error('Please review and accept the Terms & Disclaimers to create your account');
      return;
    }
    setLoading(true);
    try {
      const children = regForm.children
        .map(c => ({ name: (c.name || '').trim(), year_level: parseInt(c.year_level) }))
        .filter(c => c.name);
      await register(regForm.email, regForm.password, regForm.family_name, children, true);
      toast.success('Nau mai, haere mai! Welcome to NestWise');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex">
      {/* Left panel - branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-fern to-moana items-center justify-center p-12">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1768162178416-bad9b69afb79?w=1200')", backgroundSize: 'cover' }} />
        <div className="relative z-10 text-white max-w-lg">
          <div className="mb-8">
            <BrandMark variant="white" size={56} />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6 leading-tight">
            Kia ora, whānau
          </h1>
          <p className="text-lg text-white/90 leading-relaxed mb-8">
            A homeschool platform built for whānau teaching Years 5, 6 and 7. Original lessons in
            Pāngarau and Reo Pākehā, structured planners, and progress reports — all in one place.
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-white/90">
              <BookOpen className="w-5 h-5 flex-shrink-0" />
              <span>Original Years 5-7 lessons in two subjects</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <Users className="w-5 h-5 flex-shrink-0" />
              <span>Student &amp; parent modes for every lesson</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <Leaf className="w-5 h-5 flex-shrink-0" />
              <span>Te Reo Māori woven through the content</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - auth form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center mb-8">
            <BrandMark size={48} />
          </div>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-sand-dark rounded-full p-1">
              <TabsTrigger data-testid="login-tab" value="login" className="rounded-full data-[state=active]:bg-fern data-[state=active]:text-white">Sign In</TabsTrigger>
              <TabsTrigger data-testid="register-tab" value="register" className="rounded-full data-[state=active]:bg-fern data-[state=active]:text-white">Join</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
                <CardHeader className="pb-4">
                  <CardTitle className="font-heading text-2xl text-fern">Welcome back</CardTitle>
                  <CardDescription>Sign in to continue your learning journey</CardDescription>
                </CardHeader>
                <CardContent>
                  {!forgotMode ? (
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div>
                        <Label htmlFor="login-email">Email</Label>
                        <Input data-testid="login-email" id="login-email" type="email" required placeholder="whānau@example.com" className="rounded-xl mt-1 bg-slate-50 focus:bg-white" value={loginForm.email} onChange={e => setLoginForm(p => ({ ...p, email: e.target.value }))} />
                      </div>
                      <div>
                        <Label htmlFor="login-password">Password</Label>
                        <Input data-testid="login-password" id="login-password" type="password" required placeholder="Enter password" className="rounded-xl mt-1 bg-slate-50 focus:bg-white" value={loginForm.password} onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))} />
                      </div>
                      <button
                        type="button"
                        data-testid="forgot-password-link"
                        onClick={() => { setForgotMode(true); setForgotSent(false); setForgotEmail(loginForm.email); }}
                        className="text-sm text-fern hover:underline"
                      >
                        Forgot password?
                      </button>
                      <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-fern hover:bg-fern-light text-white rounded-full py-3 font-medium transition-all hover:scale-[1.02] active:scale-95 mt-2">
                        {loading ? 'Signing in...' : 'Sign In'}
                      </Button>
                    </form>
                  ) : forgotSent ? (
                    <div className="space-y-4" data-testid="forgot-password-sent">
                      <p className="text-sm text-slate-600">
                        If an account exists for <strong>{forgotEmail}</strong>, we've sent a link to reset your password. Check your inbox.
                      </p>
                      <Button type="button" variant="ghost" onClick={() => setForgotMode(false)} className="w-full rounded-full">
                        Back to sign in
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleForgotPassword} className="space-y-4">
                      <div>
                        <Label htmlFor="forgot-email">Email</Label>
                        <Input data-testid="forgot-email" id="forgot-email" type="email" required placeholder="whānau@example.com" className="rounded-xl mt-1 bg-slate-50 focus:bg-white" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} />
                      </div>
                      <Button data-testid="forgot-password-submit" type="submit" disabled={loading} className="w-full bg-fern hover:bg-fern-light text-white rounded-full py-3 font-medium mt-2">
                        {loading ? 'Sending...' : 'Send reset link'}
                      </Button>
                      <button type="button" onClick={() => setForgotMode(false)} className="text-sm text-slate-500 hover:underline block mx-auto">
                        Back to sign in
                      </button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="register">
              <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
                <CardHeader className="pb-4">
                  <CardTitle className="font-heading text-2xl text-fern">Nau mai, haere mai</CardTitle>
                  <CardDescription>Create your family's learning account</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <Label htmlFor="reg-family">Family Name</Label>
                      <Input data-testid="register-family" id="reg-family" required placeholder="e.g., The Ngata Whānau" className="rounded-xl mt-1 bg-slate-50 focus:bg-white" value={regForm.family_name} onChange={e => setRegForm(p => ({ ...p, family_name: e.target.value }))} />
                    </div>
                    <div>
                      <Label htmlFor="reg-email">Email</Label>
                      <Input data-testid="register-email" id="reg-email" type="email" required placeholder="whānau@example.com" className="rounded-xl mt-1 bg-slate-50 focus:bg-white" value={regForm.email} onChange={e => setRegForm(p => ({ ...p, email: e.target.value }))} />
                    </div>
                    <div>
                      <Label htmlFor="reg-password">Password</Label>
                      <Input data-testid="register-password" id="reg-password" type="password" required minLength={6} placeholder="At least 6 characters" className="rounded-xl mt-1 bg-slate-50 focus:bg-white" value={regForm.password} onChange={e => setRegForm(p => ({ ...p, password: e.target.value }))} />
                    </div>
                    <div className="space-y-2.5" data-testid="register-children-list">
                      <Label className="text-sm">Tamariki / Children</Label>
                      {regForm.children.map((child, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`register-child-row-${idx}`}>
                          <div className="col-span-7">
                            <Input
                              data-testid={`register-child-name-${idx}`}
                              placeholder={idx === 0 ? "Child's name (e.g. Aroha)" : "Another child's name"}
                              className="rounded-xl bg-slate-50 focus:bg-white"
                              value={child.name}
                              onChange={e => updateChild(idx, 'name', e.target.value)}
                            />
                          </div>
                          <div className="col-span-3">
                            <Input
                              data-testid={`register-child-year-${idx}`}
                              type="number"
                              min={1}
                              max={8}
                              placeholder="Year"
                              className="rounded-xl bg-slate-50 focus:bg-white"
                              value={child.year_level}
                              onChange={e => updateChild(idx, 'year_level', e.target.value)}
                            />
                          </div>
                          <div className="col-span-2">
                            {regForm.children.length > 1 ? (
                              <Button
                                type="button"
                                variant="ghost"
                                data-testid={`register-child-remove-${idx}`}
                                onClick={() => removeChild(idx)}
                                className="w-full rounded-xl text-slate-400 hover:text-red-500 hover:bg-red-50 px-2"
                                aria-label="Remove child"
                              >
                                ×
                              </Button>
                            ) : <div />}
                          </div>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="ghost"
                        data-testid="register-add-child"
                        onClick={addChild}
                        className="text-fern hover:text-fern hover:bg-fern/5 rounded-full text-xs gap-1 -ml-2"
                      >
                        + Add another child
                      </Button>
                      <p className="text-[11px] text-slate-400">
                        Each child has their own progress, planner and certificates. You can add more later in Family settings.
                      </p>
                    </div>
                    <div className="flex items-start gap-3 rounded-2xl border border-fern/15 bg-fern/5 px-4 py-3 mt-2">
                      <Checkbox
                        id="reg-terms"
                        data-testid="register-terms-checkbox"
                        checked={termsAccepted}
                        onCheckedChange={(v) => setTermsAccepted(v === true)}
                        className="mt-0.5 border-fern/40 data-[state=checked]:bg-fern data-[state=checked]:border-fern"
                      />
                      <Label htmlFor="reg-terms" className="text-sm text-slate-600 leading-relaxed font-normal cursor-pointer">
                        I have read and agree to NestWise's{' '}
                        <Link
                          to="/legal"
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="register-terms-link"
                          className="text-fern font-semibold hover:underline"
                        >
                          Terms &amp; Disclaimers
                        </Link>
                        , including the educational, accuracy, privacy, and consumer-rights statements.
                      </Label>
                    </div>
                    <Button data-testid="register-submit" type="submit" disabled={loading || !termsAccepted} className="w-full bg-fern hover:bg-fern-light text-white rounded-full py-3 font-medium transition-all hover:scale-[1.02] active:scale-95 mt-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100">
                      {loading ? 'Creating account...' : 'Create Account'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
